import { Component } from '@angular/core';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { ILink } from 'src/app/interfaces/ilink';
import { UserService } from 'src/app/services/general/user.service';
import {
  getLocalStorageRoleNames,
  isTeamManager,
  SelfServeLocalStorageUtil,
} from 'src/app/utils/local-storage.util';
import { DeviceService } from 'src/app/services/general/device.service';
import { NavigationEnd, Router } from '@angular/router';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoService } from '@jsverse/transloco';
import { SplitIoService } from 'src/app/services/external-tools/split-io.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { PersonalizedPracticeService } from 'src/app/services/personalized-practice/personalized-practice.service';
import { AppConstants } from 'src/app/app-constants';
@Component({
  selector: 'app-layout-general',
  standalone: false,
  templateUrl: './layout-general.component.html',
  styleUrls: ['./layout-general.component.scss'],
})
export class LayoutGeneralComponent {
  links: ILink[] = [];
  isQcAdmin: boolean = false;
  displayIpadBanner: boolean = false;
  hasSimulationBuilderAccess: boolean = false;
  hasLeaderboardAccess: boolean = false;
  hasPersonalizedPracticeAccess: boolean = false;
  private userRolesSubscription!: Subscription;

  constructor(
    private userService: UserService,
    private deviceService: DeviceService,
    private router: Router,
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private splitIoService: SplitIoService,
    private featureFlagsService: FeatureFlagsService,
    private personalizedPracticeService: PersonalizedPracticeService
  ) {}

  async ngOnInit() {
    await this.translationService.loadScopedTranslation('general');
    // Subscribing to userRoles Observable
    this.userRolesSubscription = this.userService.userRoles$.subscribe(
      (roles) => {
        this.getCurrentRole();
      }
    );
    this.checkForIpadDevice();
    // Subscribe to router events to check for iPad device
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(() => {
        this.checkForIpadDevice();
      });
  }

  ngOnDestroy() {
    if (this.userRolesSubscription) {
      this.userRolesSubscription.unsubscribe();
    }
  }

  async getCurrentRole() {
    let rolesNames: string[] = getLocalStorageRoleNames();
    this.isQcAdmin = rolesNames.includes('QCAdmin');
    this.hasSimulationBuilderAccess =
      SelfServeLocalStorageUtil.hasSimulationBuilderAccess();

    this.hasLeaderboardAccess = await this.featureFlagsService.isFlagEnabled(
      AppConstants.LEADERBOARD_UI
    );

    const featureFlagEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.PERSONALISED_PRACTICE_UI
    );
    const hasProducts = await firstValueFrom(
      this.personalizedPracticeService.hasPersonalizedPracticeProducts$
    );

    // Personalized Practice access requires both feature flag AND products availability
    this.hasPersonalizedPracticeAccess = featureFlagEnabled && hasProducts;

    const existingRelationships = localStorage.getItem('relationshipsInTeams');
    if (!existingRelationships) {
      this.userService
        .getTeamRelationshipsForCurrentUser()
        .subscribe(async (result) => {
          localStorage.setItem('relationshipsInTeams', JSON.stringify(result));
          this.links = await this.getLinksWithFeatureFlag(rolesNames);
        });
    } else {
      this.links = await this.getLinksWithFeatureFlag(rolesNames);
    }
    console.log('links', this.links);
  }

  async getLinksWithFeatureFlag(rolesNames: string[]): Promise<ILink[]> {
    const links = this.getLinks(rolesNames);

    // Check feature flag and filter dashboard link if needed
    const treatment = await this.splitIoService.getTreatment(
      AppConstants.NEW_REPORTING_UI
    );
    const myTeams = await this.splitIoService.getTreatment(
      AppConstants.MY_TEAMS_UI
    );
    const newReportingtUI = treatment === 'on';
    const myTeamsUI = myTeams === 'on';

    if (!newReportingtUI) {
      return links.filter((link) => link.route !== '/reporting_dashboard');
    }
    if (!myTeamsUI) {
      return links.filter((link) => link.route !== '/team_manager/my_teams');
    }

    // Check feature flag and filter leaderboard link if needed
    const treatmentLeaderboard = await this.splitIoService.getTreatment(
      AppConstants.LEADERBOARD_UI
    );
    const leaderboardUI = treatmentLeaderboard === 'on';

    if (!leaderboardUI) {
      return links.filter((link) => link.route !== '/leaderboard');
    }

    // Check feature flag and filter Reporting V1 links if needed
    const reportingV1Treatment = await this.splitIoService.getTreatment(
      AppConstants.REPORTING_V1
    );
    const isReportingV1Enabled = reportingV1Treatment === 'on';
    if (!isReportingV1Enabled) {
      return links.filter(
        (link) =>
          link.route !== '/reporting' &&
          link.route !== '/reporting_visualization'
      );
    }

    return links;
  }

  getLinks(rolesNames: string[]): ILink[] {
    // Adding TeamManager constraint as well to keep things consistent on frontend.
    if (isTeamManager()) {
      rolesNames.push('TeamManager');
    }

    const hasLearnerRole = rolesNames.includes('Learner');
    const hasTeamManagerRole = rolesNames.includes('TeamManager');
    const hasEnablementAdminRole = rolesNames.includes('EnablementAdmin');

    type Role =
      | 'TeamManager'
      | 'Learner'
      | 'EnablementAdmin'
      | 'Author'
      | 'AssignmentManager';

    const simulationBuilderLink: ILink = {
      route: '/self-serve',
      text: 'Simulation Builder',
      icon: 'pencil-edit',
      childRoutes: [],
    };

    const leaderboardLink: ILink = {
      route: '/leaderboard',
      text: this.translocoService.translate('LEADERBOARD', {}, 'general'),
      icon: 'award',
      childRoutes: [],
    };

    const personalizedPracticeLink: ILink = {
      route: '/personalized-practice',
      text: this.translocoService.translate(
        'PERSONALIZED_PRACTICE',
        {},
        'general'
      ),
      icon: 'grid',
      childRoutes: [],
    };

    const roleLinks: Partial<Record<Role, ILink[]>> = {
      TeamManager: [
        {
          route: '/team_manager/my_teams',
          text: this.translocoService.translate('MY_TEAMS', {}, 'general'),
          icon: 'users',
          childRoutes: [],
        },
        {
          route: '/reporting_dashboard',
          text: this.translocoService.translate('DASHBOARD', {}, 'general'),
          icon: 'users',
          childRoutes: [],
        },
      ],
      Learner: [
        {
          route: '/assignments',
          text: `${this.translocoService.translate(
            'ASSIGNMENTS',
            {},
            'general'
          )}`,
          icon: 'grid',
          childRoutes: [
            '/assignment',
            '/results_conversational',
            '/results_guided',
            '/results_visual_aid',
            '/learning-pathway',
          ],
        },
      ],
      EnablementAdmin: [
        {
          route: '/reporting_dashboard',
          text: this.translocoService.translate('DASHBOARD', {}, 'general'),
          icon: 'users',
          childRoutes: [],
        },
        {
          route: '/reporting',
          text: `${this.translocoService.translate(
            'REPORTING',
            {},
            'general'
          )}`,
          icon: 'bar-chart-07',
          childRoutes: ['/reporting_visualization'],
        },
      ],
    };

    const orderedRoles: Role[] = [
      'TeamManager',
      'Learner',
      'EnablementAdmin',
      'Author',
      'AssignmentManager',
    ];

    let links: ILink[] = [];

    orderedRoles.forEach((role) => {
      if (rolesNames.includes(role) && roleLinks[role]) {
        roleLinks[role]?.forEach((link) => {
          if (
            !links.some((existingLink) => existingLink.route === link.route)
          ) {
            links.push(link);
          }
        });

        if (role === 'Learner' && this.hasPersonalizedPracticeAccess) {
          links.push(personalizedPracticeLink);
        }
      }
    });

    if (this.hasSimulationBuilderAccess) {
      links.push(simulationBuilderLink);
    }

    if (
      this.hasLeaderboardAccess &&
      (hasLearnerRole || hasTeamManagerRole || hasEnablementAdminRole)
    ) {
      links.push(leaderboardLink);
    }

    return links;
  }

  checkForIpadDevice(): void {
    const ua: string = navigator.userAgent.toLowerCase();

    // Check for iPad in the user agent string (covers older iOS devices)
    const isIpadOld: boolean = ua.includes('ipad');

    // Check for iPad based on the new iOS/macOS-like user agent string but with touch support
    const isIpadNew: boolean = /macintosh/.test(ua) && 'ontouchend' in document;

    // Return true if either condition is met
    const isIpad = isIpadNew || isIpadOld;
    this.displayIpadBanner = isIpad && this.shouldDisplayIpadBanner();
    this.deviceService.setIpadStatus(this.displayIpadBanner);
  }

  shouldDisplayIpadBanner(): boolean {
    const currentRoute = this.router.url;
    if (currentRoute.includes('/call')) {
      // Hide the banner if the current route is '/call'
      return false;
    }
    return true;
  }
}
